import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  Button,
  Card,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from "@rumbledirect/ui-v2";
import {
  ContractPlanningFilters,
  type ContractPlanningFilterValues,
} from "@/contractPlanning/ContractPlanningFilters";
import {
  defaultContractPlanningPeriod,
  isPlanningPeriodOrdered,
  isPlanningPeriodWithinLimit,
} from "@/contractPlanning/contractPlanningPeriod";
import { ContractPlanningWeekRow } from "@/contractPlanning/ContractPlanningWeekRow";
import {
  type PlanningWeekView,
  toPlanningWeekViews,
} from "@/contractPlanning/contractPlanningWeeks";
import { useGetAdminContractMonitorPlanning } from "@/lib/api/generated/contract-monitor/contract-monitor";
import { toPeriodDate } from "@/lib/periodDate";

/** Week, periode, actief, start, eindigt, plus the expand toggle. */
const COLUMN_COUNT = 6;

/** Enough skeleton rows to fill the fold without pretending to know the count. */
const SKELETON_ROW_KEYS = Array.from(
  { length: 8 },
  (_, index) => `contract-planning-skeleton-${index}`,
);

export const Route = createFileRoute("/_authenticated/contract-planning")({
  // The whole filter state lives in the URL so a view can be shared or
  // bookmarked. The period is filled in with a default rather than left empty,
  // because the backend requires both bounds.
  validateSearch: (
    search: Record<string, unknown>,
  ): ContractPlanningFilterValues => {
    const fallback = defaultContractPlanningPeriod();

    return {
      from: toPeriodDate(search.from) ?? fallback.from,
      to: toPeriodDate(search.to) ?? fallback.to,
      site:
        typeof search.site === "string" && search.site !== ""
          ? search.site
          : undefined,
    };
  },
  component: ContractPlanningPage,
});

function ContractPlanningPage(): ReactNode {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { from, to, site } = search;

  // An inverted or over-long period is a user mistake, not a question: the
  // filter bar says what is wrong and the request stays unsent.
  const periodUsable =
    isPlanningPeriodOrdered(search) && isPlanningPeriodWithinLimit(search);

  const { data, isPending, isError, refetch } =
    useGetAdminContractMonitorPlanning(
      { from, to, ...(site ? { site } : {}) },
      { query: { enabled: periodUsable } },
    );

  const weeks = toPlanningWeekViews(data?.weeks);

  const applyFilters = (patch: Partial<ContractPlanningFilterValues>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Contractplanning
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Per week het aantal lopende contracten, en hoeveel er starten en
          eindigen. De weken lopen door tot en met de week waarin de einddatum
          valt.
        </p>
      </div>

      <ContractPlanningFilters values={search} onChange={applyFilters} />

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th>Week</Th>
              <Th>Periode</Th>
              <Th>Actief</Th>
              <Th className="hidden sm:table-cell">Start</Th>
              <Th className="hidden sm:table-cell">Eindigt</Th>
              <Th className="w-12">
                <span className="sr-only">Locaties tonen</span>
              </Th>
            </Tr>
          </THead>
          <TBody>
            <ContractPlanningRows
              isPending={isPending && periodUsable}
              isError={isError}
              periodUsable={periodUsable}
              weeks={weeks}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>
    </div>
  );
}

function ContractPlanningRows({
  isPending,
  isError,
  periodUsable,
  weeks,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly periodUsable: boolean;
  readonly weeks: readonly PlanningWeekView[];
  readonly onRetry: () => void;
}): ReactNode {
  if (!periodUsable) {
    return (
      <Tr>
        <Td colSpan={COLUMN_COUNT} className="py-10 text-center">
          <p className="text-muted-foreground">
            Pas de periode aan om de planning te zien.
          </p>
        </Td>
      </Tr>
    );
  }

  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        <Td>
          <Skeleton className="h-4 w-12" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-24" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-40" />
        </Td>
        <Td className="hidden sm:table-cell">
          <Skeleton className="h-4 w-8" />
        </Td>
        <Td className="hidden sm:table-cell">
          <Skeleton className="h-4 w-8" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-4" />
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={COLUMN_COUNT} className="py-10 text-center">
          <div className="space-y-3" role="alert">
            <p className="text-muted-foreground">
              We konden de planning niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (weeks.length === 0) {
    return (
      <Tr>
        <Td colSpan={COLUMN_COUNT} className="py-10 text-center">
          <p className="text-muted-foreground">Geen weken in deze periode.</p>
        </Td>
      </Tr>
    );
  }

  return weeks.map((week) => (
    <ContractPlanningWeekRow
      key={week.key}
      week={week}
      columnCount={COLUMN_COUNT}
    />
  ));
}
